import type { InscriptionDraft } from "./types";

export type Brc20Operation = "deploy" | "mint" | "transfer";

export interface Brc20Fields {
  operation: Brc20Operation;
  ticker: string;
  amount?: string;
  maxSupply?: string;
  mintLimit?: string;
  decimals?: number;
}

function positiveInteger(value: string | undefined, label: string): string {
  if (!value || !/^\d+$/.test(value) || BigInt(value) <= 0n) throw new Error(`${label} must be a positive whole number`);
  return value;
}

export function buildBrc20Payload(fields: Brc20Fields): string {
  const ticker = fields.ticker.trim();
  if (!ticker || new TextEncoder().encode(ticker).length !== 4) {
    throw new Error("BRC-20 ticker must be exactly four bytes");
  }

  if (fields.operation === "deploy") {
    const payload: Record<string, string> = {
      p: "brc-20",
      op: "deploy",
      tick: ticker,
      max: positiveInteger(fields.maxSupply, "Maximum supply"),
      lim: positiveInteger(fields.mintLimit, "Mint limit"),
    };
    if (fields.decimals !== undefined) {
      if (!Number.isInteger(fields.decimals) || fields.decimals < 0 || fields.decimals > 18) {
        throw new Error("Decimals must be between 0 and 18");
      }
      payload.dec = String(fields.decimals);
    }
    return JSON.stringify(payload);
  }

  return JSON.stringify({
    p: "brc-20",
    op: fields.operation,
    tick: ticker,
    amt: positiveInteger(fields.amount, "Amount"),
  });
}

export function createBrc20Drafts(fields: Brc20Fields, repeats = 1): InscriptionDraft[] {
  if (!Number.isInteger(repeats) || repeats < 1 || repeats > 500) throw new Error("Repeat count must be between 1 and 500");
  const payload = buildBrc20Payload(fields);
  return Array.from({ length: repeats }, (_, index) => ({
    id: crypto.randomUUID(),
    name: `${fields.ticker.toLowerCase()}-${fields.operation}-${index + 1}.json`,
    mimeType: "application/json",
    body: new TextEncoder().encode(payload),
    metadata: { protocol: "brc-20", operation: fields.operation },
  }));
}
